/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { format } from 'date-fns';
import { type ChangeEvent, useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button.js';
import { Modal } from '../../components/ui/modal.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { type Proforma } from '../../lib/api/proformas.js';
import {
  useAdjustProforma,
  useGenerateProformaCycle,
  useProformas,
  useSendProforma,
} from '../../lib/queries/proformas.js';
import { rupeesToPaise } from '../../lib/utils/money.js';
import { FinanceProformaTable } from '../finance/finance-proforma-table.js';

import { ApprovedActions, ConvertApprovedButton } from './proforma-convert.js';

const INPUT =
  'focus-ring w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white';
const LABEL = 'mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300';

function GenerateCycleModal({
  open,
  onClose,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const generate = useGenerateProformaCycle();
  const [month, setMonth] = useState(() => format(new Date(), 'yyyy-MM'));
  const [fee, setFee] = useState('');

  const onMonth = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setMonth(e.target.value);
  }, []);
  const onFee = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setFee(e.target.value);
  }, []);
  const onModal = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        onClose();
      }
    },
    [onClose],
  );
  const onGenerate = useCallback(() => {
    const rupees = Number(fee);
    if (!Number.isFinite(rupees) || rupees <= 0) {
      return;
    }
    generate.mutate({ month, sessionFeePaise: rupeesToPaise(rupees) }, { onSuccess: onClose });
  }, [fee, month, generate, onClose]);

  return (
    <Modal
      open={open}
      onOpenChange={onModal}
      title={t('proforma.generateTitle')}
      closeLabel={t('common.cancel')}
    >
      <div className="space-y-3">
        <p className="text-xs text-slate-500 dark:text-slate-400">{t('proforma.generateHint')}</p>
        <div>
          <label htmlFor="pf-month" className={LABEL}>
            {t('proforma.month')}
          </label>
          <input id="pf-month" type="month" className={INPUT} value={month} onChange={onMonth} />
        </div>
        <div>
          <label htmlFor="pf-fee" className={LABEL}>
            {t('proforma.sessionFee')}
          </label>
          <input
            id="pf-fee"
            type="number"
            min={1}
            className={INPUT}
            value={fee}
            onChange={onFee}
            placeholder="1800"
          />
        </div>
        <div className="flex justify-end">
          <Button
            type="button"
            onClick={onGenerate}
            disabled={fee.trim() === '' || generate.isPending}
          >
            {t('proforma.generate')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function AdjustModal({
  proforma,
  onClose,
}: {
  readonly proforma: Proforma;
  readonly onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const adjust = useAdjustProforma();
  const [sessions, setSessions] = useState(String(proforma.sessionsCount));
  const [fee, setFee] = useState(String(proforma.sessionFeePaise / 100));
  const [discount, setDiscount] = useState(String(proforma.discountPaise / 100));

  const onSessions = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSessions(e.target.value);
  }, []);
  const onFee = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setFee(e.target.value);
  }, []);
  const onDiscount = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setDiscount(e.target.value);
  }, []);
  const onModal = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        onClose();
      }
    },
    [onClose],
  );
  const onSave = useCallback(() => {
    adjust.mutate(
      {
        id: proforma.id,
        input: {
          sessionsCount: Number(sessions),
          sessionFeePaise: rupeesToPaise(Number(fee)),
          discountPaise: rupeesToPaise(Number(discount)),
        },
      },
      { onSuccess: onClose },
    );
  }, [adjust, proforma.id, sessions, fee, discount, onClose]);

  return (
    <Modal
      open
      onOpenChange={onModal}
      title={t('proforma.adjustTitle')}
      closeLabel={t('common.cancel')}
    >
      <div className="space-y-3">
        <div>
          <label htmlFor="adj-sessions" className={LABEL}>
            {t('proforma.sessions')}
          </label>
          <input
            id="adj-sessions"
            type="number"
            min={0}
            className={INPUT}
            value={sessions}
            onChange={onSessions}
          />
        </div>
        <div>
          <label htmlFor="adj-fee" className={LABEL}>
            {t('proforma.sessionFee')}
          </label>
          <input
            id="adj-fee"
            type="number"
            min={1}
            className={INPUT}
            value={fee}
            onChange={onFee}
          />
        </div>
        <div>
          <label htmlFor="adj-discount" className={LABEL}>
            {t('proforma.discount')}
          </label>
          <input
            id="adj-discount"
            type="number"
            min={0}
            className={INPUT}
            value={discount}
            onChange={onDiscount}
          />
        </div>
        <div className="flex justify-end">
          <Button type="button" onClick={onSave} disabled={adjust.isPending}>
            {t('common.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function DraftActions({ proforma }: { readonly proforma: Proforma }): JSX.Element {
  const { t } = useTranslation();
  const send = useSendProforma();
  const [adjusting, setAdjusting] = useState(false);
  const openAdjust = useCallback(() => {
    setAdjusting(true);
  }, []);
  const closeAdjust = useCallback(() => {
    setAdjusting(false);
  }, []);
  const onSend = useCallback(() => {
    send.mutate(proforma.id);
  }, [send, proforma.id]);

  return (
    <>
      <Button type="button" variant="outline" onClick={openAdjust}>
        {t('proforma.adjust')}
      </Button>
      <Button type="button" onClick={onSend} disabled={send.isPending}>
        {t('proforma.send')}
      </Button>
      {adjusting && <AdjustModal proforma={proforma} onClose={closeAdjust} />}
    </>
  );
}

/** Admin → Proformas (BRD v2.0 CR-008/CR-009): generate cycle, adjust/send, convert approved. */
/** Per-row actions for the admin proforma table: send a draft, convert an approved one. */
function renderProformaActions(p: Proforma): JSX.Element | null {
  if (p.status === 'draft') {
    return <DraftActions proforma={p} />;
  }
  if (p.status === 'approved') {
    return <ApprovedActions proforma={p} />;
  }
  return null;
}

export function AdminProformasPage(): JSX.Element {
  const { t } = useTranslation();
  const query = useProformas();
  const [generating, setGenerating] = useState(false);
  const openGenerate = useCallback(() => {
    setGenerating(true);
  }, []);
  const closeGenerate = useCallback(() => {
    setGenerating(false);
  }, []);

  return (
    <div className="p-8">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <PageHeader title={t('proforma.adminTitle')} subtitle={t('proforma.adminSubtitle')} />
        <div className="flex flex-wrap gap-2">
          <ConvertApprovedButton proformas={query.data ?? []} />
          <Button type="button" onClick={openGenerate}>
            {t('proforma.generateCycle')}
          </Button>
        </div>
      </div>

      <FinanceProformaTable rowActions={renderProformaActions} />

      <GenerateCycleModal open={generating} onClose={closeGenerate} />
    </div>
  );
}
